import React from 'react';
import {FormattedMessage} from 'react-intl';

import './styles.scss';

export interface ITodayActivityItem {
  key: string
  title: string
  context?: string
  time?: string | null
  isScreener?: boolean
  onClick?: () => void
}

function isSameLocalDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

// A step belongs in "today's activity" when it has no schedule (available now) or is scheduled for
// today or earlier (overdue steps are still actionable, so they must not disappear).
export function isDueToday(iso: string | null | undefined): boolean {
  if (!iso) {
    return true;
  }

  const endOfToday = new Date();

  endOfToday.setHours(23, 59, 59, 999);

  return new Date(iso).getTime() <= endOfToday.getTime();
}

function formatTime(iso: string): string {
  const date = new Date(iso);

  if (isSameLocalDay(date, new Date())) {
    return date.toLocaleTimeString(undefined, {hour: '2-digit', minute: '2-digit'});
  }

  return date.toLocaleString(undefined, {month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'});
}

export const TodayActivity: React.FC<{items: ITodayActivityItem[]}> = ({items}) => {
  if (items.length === 0) {
    return (
      <p className="today-activity__empty">
        <FormattedMessage id="gritx.todayActivity.empty" defaultMessage="Nothing scheduled for today."/>
      </p>
    );
  }

  return (
    <ul className="today-activity">
      {items.map((item) => {
        const body = (
          <>
            <div className="today-activity__row">
              <span className="today-activity__title">{item.title}</span>
              {item.isScreener && (
                <span className="today-activity__badge">
                  <FormattedMessage id="gritx.interventions.screener" defaultMessage="Screener"/>
                </span>
              )}
            </div>
            {(item.context || item.time) && (
              <div className="today-activity__meta">
                {item.context && <span className="today-activity__chip">{item.context}</span>}
                {item.time && <span className="today-activity__time">{formatTime(item.time)}</span>}
              </div>
            )}
          </>
        );

        if (!item.onClick) {
          return <li key={item.key} className="today-activity__item">{body}</li>;
        }

        return (
          <li key={item.key}>
            <button type="button" className="today-activity__item today-activity__item--action" onClick={item.onClick}>
              {body}
              <span className="today-activity__chevron" aria-hidden="true">›</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
};
